import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ActivityHeatmap } from 'react-activity-heatmap';
import supabase from '../utils/supabaseClient';
import './Dashboard.css';

const getLocalSubmissions = (userId) => {
    if (!userId) return [];
    try {
        const raw = localStorage.getItem(`mathbox_local_submissions_${userId}`);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const DEFAULT_COMMUNITY_STATS = [
    { label: 'Views', value: 0, helperLabel: 'Last week', helperValue: 0 },
    { label: 'Solutions', value: 0, helperLabel: 'Last week', helperValue: 0 },
    { label: 'Discuss', value: 0, helperLabel: 'Last week', helperValue: 0 },
    { label: 'Reputation', value: 0, helperLabel: 'Last week', helperValue: 0 },
];

const Dashboard = () => {
    const { isDarkTheme } = useTheme();
    const { user, profile, signOut, loading, profileLoading } = useAuth();
    const navigate = useNavigate();

    const handleSignOut = async () => {
        try {
            await signOut();
            setTimeout(() => {
                navigate('/login', { replace: true });
            }, 100);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const isLoading = loading || profileLoading;

    // ---- Real database stats ----
    const [dbStats, setDbStats] = useState(null);
    const [dbRecentExercises, setDbRecentExercises] = useState([]);
    const [dbDifficulty, setDbDifficulty] = useState([]);
    const [dbUnitsCount, setDbUnitsCount] = useState({ completed: 0, total: 0 });
    const [dbActivityData, setDbActivityData] = useState([]);
    const [statsLoading, setStatsLoading] = useState(true);

    const fetchDashboardData = useCallback(async () => {
        if (!user) return;
        setStatsLoading(true);
        try {
            // 1. Total exercises count
            const { data: allExercises } = await supabase
                .from('exercises')
                .select('id')
                .eq('status', 'approved');

            const totalExercises = allExercises?.length || 0;

            // 2. Units counts
            const { count: totalUnits } = await supabase
                .from('units')
                .select('*', { count: 'exact', head: true });

            setDbUnitsCount({ completed: 0, total: totalUnits || 0 });

            // 3. Try to fetch submissions (table may not exist yet)
            let submissions = [];
            try {
                const { data, error } = await supabase
                    .from('submissions')
                    .select('exercise_id, is_correct, points, created_at')
                    .eq('user_id', user.id);
                if (!error && data) submissions = data;
            } catch (e) {
                // submissions table doesn't exist yet — that's fine
            }

            // Fallback to local submissions if DB has none or is blocked by RLS
            if (!submissions || submissions.length === 0) {
                submissions = getLocalSubmissions(user.id);
            }

            const correctSubs = submissions.filter(s => s.is_correct) || [];
            const uniqueSolved = new Set(correctSubs.map(s => s.exercise_id));
            const totalScore = correctSubs.reduce((sum, s) => sum + (s.points || 0), 0);

            setDbDifficulty([
                { key: 'easy', label: 'Easy', solved: 0, total: totalExercises },
                { key: 'medium', label: 'Medium', solved: 0, total: 0 },
                { key: 'hard', label: 'Hard', solved: 0, total: 0 },
            ]);

            setDbStats({
                exercisesCompleted: uniqueSolved.size,
                totalExercises,
                score: totalScore,
            });

            // 4. Recent exercises from submissions
            if (submissions.length > 0) {
                const recentSubs = [...submissions]
                    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                    .slice(0, 10);
                const exerciseIds = [...new Set(recentSubs.map(s => s.exercise_id))];
                const { data: exerciseDetails } = await supabase
                    .from('exercises')
                    .select('id, title, tags')
                    .in('id', exerciseIds);

                const exerciseMap = {};
                (exerciseDetails || []).forEach(e => { exerciseMap[e.id] = e; });

                const mapped = recentSubs.map(s => {
                    const ex = exerciseMap[s.exercise_id] || {};
                    const d = new Date(s.created_at);
                    return {
                        id: s.exercise_id + '_' + s.created_at,
                        date: d.toLocaleDateString(),
                        time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        title: ex.title || 'Exercise',
                        topic: ex.tags?.[0] || 'Math',
                        points: s.is_correct ? (s.points || 3) : 0,
                    };
                });
                setDbRecentExercises(mapped);

                // 5. Activity heatmap from submissions
                const dayMap = {};
                submissions.forEach(s => {
                    const day = s.created_at?.split('T')[0];
                    if (day) dayMap[day] = (dayMap[day] || 0) + 1;
                });
                const actData = Object.entries(dayMap).map(([date, count]) => ({
                    date,
                    count,
                    level: count === 0 ? 0 : count < 3 ? 1 : count < 6 ? 2 : count < 10 ? 3 : 4,
                })).sort((a, b) => a.date.localeCompare(b.date));
                setDbActivityData(actData);
            }
        } catch (err) {
            console.error('[Dashboard] Error fetching stats:', err);
        } finally {
            setStatsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user && !isLoading) {
            fetchDashboardData();
        }
    }, [user, isLoading, fetchDashboardData]);

    if (!user && !isLoading) {
        return (
            <div className="dashboard dashboard--empty">
                <div className="empty-state-card">
                    <h1>Welcome to MathBox</h1>
                    <p>Please sign in to explore your personalised dashboard.</p>
                </div>
            </div>
        );
    }

    const displayName =
        profile?.display_name ||
        user?.user_metadata?.display_name ||
        user?.email?.split('@')[0] ||
        'Learner';

    const stats = {
        exercisesCompleted: dbStats?.exercisesCompleted ?? profile?.stats?.exercisesCompleted ?? 0,
        totalExercises: dbStats?.totalExercises ?? profile?.stats?.totalExercises ?? 0,
        problemsCompleted: dbStats?.exercisesCompleted ?? profile?.stats?.problemsCompleted ?? 0,
        totalProblems: dbStats?.totalExercises ?? profile?.stats?.totalProblems ?? 0,
        unitsCompleted: dbUnitsCount.completed ?? profile?.stats?.unitsCompleted ?? 0,
        totalUnits: dbUnitsCount.total ?? profile?.stats?.totalUnits ?? 0,
        score: dbStats?.score ?? profile?.score ?? 0,
        currency: profile?.currency ?? 0,
        currencyLabel: profile?.currencyLabel || profile?.currency_label || 'MathCoins',
    };

    const activityData = dbActivityData.length > 0 ? dbActivityData : (profile?.activity?.data || []);

    const totalActiveDays = activityData.length;
    // Calculate max streak from activity data
    const maxStreak = (() => {
        if (activityData.length === 0) return 0;
        let max = 1, current = 1;
        const sorted = [...activityData].sort((a, b) => a.date.localeCompare(b.date));
        for (let i = 1; i < sorted.length; i++) {
            const prev = new Date(sorted[i - 1].date);
            const curr = new Date(sorted[i].date);
            const diff = (curr - prev) / (1000 * 60 * 60 * 24);
            if (diff === 1) { current++; max = Math.max(max, current); }
            else if (diff > 1) { current = 1; }
        }
        return max;
    })();

    const recentExercises = dbRecentExercises.length > 0
        ? dbRecentExercises
        : Array.isArray(profile?.recentExercises) && profile.recentExercises.length > 0
            ? profile.recentExercises
            : [];

    const rawDifficulty = dbDifficulty.length > 0
        ? dbDifficulty
        : Array.isArray(profile?.difficultyStats) && profile.difficultyStats.length > 0
            ? profile.difficultyStats
            : Array.isArray(profile?.difficulty_breakdown) && profile.difficulty_breakdown.length > 0
                ? profile.difficulty_breakdown
                : [{ key: 'easy', label: 'Easy', solved: 0, total: 0 }, { key: 'medium', label: 'Medium', solved: 0, total: 0 }, { key: 'hard', label: 'Hard', solved: 0, total: 0 }];

    const difficultyStats = rawDifficulty.map((item) => {
        const label = item.label || item.name || item.tier || item.key || 'Difficulty';
        const solved = Number.isFinite(item.solved)
            ? item.solved
            : Number(item.completed ?? item.value ?? 0);
        const total = Number.isFinite(item.total)
            ? item.total
            : Number(item.outOf ?? item.out_of ?? item.max ?? item.total ?? 0);
        const key = (item.key || label).toString().toLowerCase().replace(/[^a-z]+/g, '-');
        return {
            key: key || 'difficulty',
            label,
            solved: Number.isFinite(solved) ? solved : 0,
            total: Number.isFinite(total) ? total : 0,
        };
    });

    const communityStats = Array.isArray(profile?.communityStats) && profile.communityStats.length > 0
        ? profile.communityStats.map((stat) => ({
            label: stat.label || stat.name,
            value: stat.value ?? stat.count ?? 0,
            helperLabel: stat.helperLabel || stat.subLabel || stat.trendLabel || 'Last week',
            helperValue: stat.helperValue ?? stat.subValue ?? stat.trendValue ?? 0,
        }))
        : DEFAULT_COMMUNITY_STATS.map((stat) => {
            const key = stat.label.toLowerCase();
            const community = profile?.community || {};
            const baseValue = community[key];
            const delta = community[`${key}Delta`] ?? community[`${key}_delta`];
            return {
                ...stat,
                value: typeof baseValue === 'number' ? baseValue : stat.value,
                helperValue: typeof delta === 'number' ? delta : stat.helperValue,
            };
        });

    const badgeCount = typeof profile?.badges === 'number'
        ? profile.badges
        : Array.isArray(profile?.badges?.earned)
            ? profile.badges.earned.length
            : profile?.badges?.count ?? 0;

    const lockedBadge = profile?.badges?.locked || profile?.badges?.next || 'Autumn Math Challenge';

    const attempting = profile?.stats?.attempting ?? profile?.stats?.inProgress ?? 1;
    const solvedRatio = stats.totalExercises > 0 ? Math.min(Math.max(stats.exercisesCompleted / stats.totalExercises, 0), 1) : 0;

    const formatValue = (value) => {
        if (value === null || value === undefined) {
            return '0';
        }
        if (typeof value === 'number') {
            return value.toLocaleString();
        }
        return value;
    };

    const avatarInitial = displayName.charAt(0).toUpperCase();
    const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url || user?.user_metadata?.picture || null;
    const username = profile?.username || profile?.handle || user?.email || displayName;
    const rankValue = profile?.rank ?? profile?.stats?.rank ?? 5000000;

    return (
        <div className={`dashboard ${isDarkTheme ? 'dark' : 'light'}`}>
            {isLoading || statsLoading ? (
                <div className="dashboard-loading">Loading dashboard...</div>
            ) : (
                <div className="dashboard-layout">
                    <aside className="profile-panel card">
                        <div className="profile-header">
                            <div className="profile-overview">
                                <div className={`avatar-shell${avatarUrl ? ' with-image' : ''}`} aria-hidden={!!avatarUrl}>
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt={`${displayName}'s avatar`} />
                                    ) : (
                                        avatarInitial
                                    )}
                                </div>
                                <div className="profile-info">
                                    <div className="profile-name-group">
                                        <h1>{displayName}</h1>
                                        <span className="profile-username">{username}</span>
                                    </div>
                                    <div className="profile-rank">
                                        <span className="rank-label">Rank</span>
                                        <span className="rank-value">~{formatValue(rankValue)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="profile-actions">
                            <button className="edit-button">Edit Profile</button>
                            <button className="sign-out-link" onClick={handleSignOut}>
                                Sign Out
                            </button>
                        </div>

                        <div className="profile-section">
                            <h3 className="section-title">Community Stats</h3>
                            <div className="stat-list">
                                {communityStats.map((stat, index) => (
                                    <div key={index} className="stat-item">
                                        <div className="stat-details">
                                            <span className="stat-label">{stat.label}</span>
                                            <span className="stat-helper">
                                                {stat.helperLabel} {formatValue(stat.helperValue)}
                                            </span>
                                        </div>
                                        <span className="stat-value">{formatValue(stat.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>

                    <main className="main-panel">
                        <div className="practice-cta-card card" onClick={() => navigate('/practice')}>
                            <div className="practice-cta-left">
                                <h2>Start Practicing</h2>
                                <p>Browse exercises by unit, solve problems, and track your progress.</p>
                            </div>
                            <button className="practice-cta-btn" onClick={() => navigate('/practice')}>Practice Now →</button>
                        </div>

                        <div className="practice-cta-card lesson-cta-card card" onClick={() => navigate('/learn')}>
                            <div className="practice-cta-left">
                                <h2>Study Lessons</h2>
                                <p>Read lesson content by unit and review key concepts before solving exercises.</p>
                            </div>
                            <button className="practice-cta-btn" onClick={() => navigate('/learn')}>Open Lessons →</button>
                        </div>

                        <section className="main-top">
                            <div className="progress-card card">
                                <div className="progress-ring">
                                    <div className="ring">
                                        <div className="ring-center">
                                            <span className="ring-value">{Math.round(solvedRatio * 100)}%</span>
                                            <span className="ring-total">{stats.exercisesCompleted}/{stats.totalExercises}</span>
                                            <span className="ring-caption">SOLVED</span>
                                        </div>
                                        <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                            <circle
                                                cx="80"
                                                cy="80"
                                                r="70"
                                                fill="none"
                                                stroke={isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                                strokeWidth="12"
                                            />
                                            <circle
                                                cx="80"
                                                cy="80"
                                                r="70"
                                                fill="none"
                                                stroke={isDarkTheme ? '#4ade80' : '#16a34a'}
                                                strokeWidth="12"
                                                strokeDasharray={`${2 * Math.PI * 70}`}
                                                strokeDashoffset={`${2 * Math.PI * 70 * (1 - solvedRatio)}`}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="progress-info">
                                    <div className="attempting-row">
                                        <span className="attempting-count">{attempting}</span>
                                        <span className="attempting-label">ATTEMPTING</span>
                                    </div>
                                    <div className="difficulty-breakdown">
                                        {difficultyStats.map((diff) => (
                                            <div key={diff.key} className="difficulty-row">
                                                <span className={`difficulty-label ${diff.key}`}>
                                                    {diff.label}
                                                </span>
                                                <span className="difficulty-stats">
                                                    {diff.solved} / {diff.total}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="badges-card card">
                                <div className="badges-header">
                                    <h2>Badges</h2>
                                    <span className="badge-count">{badgeCount}</span>
                                </div>
                                <div className="badge-body">
                                    <span className="badge-subtitle">LOCKED BADGE</span>
                                    <div className="badge-name">{lockedBadge}</div>
                                </div>
                            </div>
                        </section>

                        <section className="heatmap-card card">
                            <div className="heatmap-header">
                                <div>
                                    <h2>Practice Activity</h2>
                                    <p className="subtitle" style={{ opacity: 0.6, fontSize: '0.9rem' }}>Past year</p>
                                </div>
                                <div className="streak-info" style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>
                                    <span>Total active days: {totalActiveDays}</span>
                                    <span>Max streak: {maxStreak}</span>
                                </div>
                            </div>
                            <div className="heatmap-container">
                                <ActivityHeatmap
                                    activities={activityData}
                                    cellColors={isDarkTheme ? {
                                        level0: '#27272a',
                                        level1: '#064e3b',
                                        level2: '#065f46',
                                        level3: '#10b981',
                                        level4: '#34d399'
                                    } : {
                                        level0: '#f4f4f5',
                                        level1: '#d1fae5',
                                        level2: '#6ee7b7',
                                        level3: '#10b981',
                                        level4: '#047857'
                                    }}
                                    endDate={new Date()}
                                    startDate={new Date(new Date().setDate(new Date().getDate() - 365))}
                                />
                            </div>
                        </section>

                        <section className="submissions-card card">
                            <div className="submissions-header">
                                <h2>Recent Solves</h2>
                                <div className="submissions-tabs">
                                    <button className="tab active">All</button>
                                    <button className="tab">Problems</button>
                                </div>
                            </div>
                            <div className="submission-list">
                                {recentExercises.length === 0 ? (
                                    <div className="submission-empty">
                                        <p>No exercises solved yet. <a href="/practice">Start practicing!</a></p>
                                    </div>
                                ) : (
                                    recentExercises.map((ex) => (
                                        <div key={ex.id} className="submission-item">
                                            <div className="submission-details">
                                                <span className="submission-title">{ex.title}</span>
                                                <div className="submission-meta">
                                                    <span className="submission-topic">{ex.topic}</span>
                                                    <span className="submission-time">{ex.date} · {ex.time}</span>
                                                </div>
                                            </div>
                                            <div className="submission-right">
                                                <span className={`submission-points ${ex.points >= 0 ? 'positive' : 'negative'}`}>
                                                    {ex.points >= 0 ? `+${ex.points}` : ex.points}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </section>
                    </main>
                </div>
            )}
        </div>
    );
};

export default Dashboard;